import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { useNavigate } from "react-router-dom";
import { BASE_URL } from '../apiConfig';
import Cookies from 'js-cookie';
import Loading from '../element/Loading';
import loginBg from '../imgs/bg-login.jpeg';
import { useTranslation } from '../LanguageContext';
import { useAuth } from '../AuthContext';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
];

export default function Page() {
  const { t, lang, setLang } = useTranslation();
  const { setUser } = useAuth();
  const route = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const refInput = useRef();

  useEffect(() => {
    refInput.current.focus();
  }, []);

  const Login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/login`, { email, password });
      if (res.data.code == 200) {
        Cookies.set('WEB_SSR', res.data.access_token, { expires: 1 / 12 });
        if (res.data.user) {
          setUser(res.data.user);
        }
        toast.success(t('login.success'));
        route('/dashboard');
      } else {
        toast.error(t('login.error'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('login.error.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#f0ede5' }}>
      <div className="w-full max-w-7xl h-[85vh] rounded-2xl overflow-hidden shadow-2xl grid grid-cols-1 md:grid-cols-2" style={{ minHeight: '520px' }}>

        <div className="flex flex-col justify-center px-20 max-lg:px-10 py-12" style={{ background: '#faf8f2' }}>
          <p className="text-xs tracking-widest mb-1" style={{ color: '#b0a98a' }}>{t('login.welcome')}</p>
          <h1 className="text-2xl font-bold mb-1" style={{ color: '#1a1916' }}>{t('login.title')}</h1>
          <p className="text-sm mb-8" style={{ color: '#8a8578' }}>{t('login.subtitle')}</p>

          <form onSubmit={Login} className="space-y-4">
            <div>
              <label className="block text-xs mb-1.5" style={{ color: '#8a8578' }}>{t('login.username')}</label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#b0a98a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <input
                  type="text"
                  ref={refInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.username.placeholder')}
                  required
                  className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border transition-all outline-none"
                  style={{
                    background: '#fff',
                    border: '1.5px solid #e0dbd0',
                    color: '#1a1916',
                  }}
                  onFocus={e => e.target.style.borderColor = '#e6c84a'}
                  onBlur={e => e.target.style.borderColor = '#e0dbd0'}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs" style={{ color: '#8a8578' }}>{t('login.password')}</label>
                <a href="#" className="text-xs font-medium" style={{ color: '#c4a020' }}>{t('login.password.forgot')}</a>
              </div>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#b0a98a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.password.placeholder')}
                  required
                  className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border transition-all outline-none"
                  style={{
                    background: '#fff',
                    border: '1.5px solid #e0dbd0',
                    color: '#1a1916',
                  }}
                  onFocus={e => e.target.style.borderColor = '#e6c84a'}
                  onBlur={e => e.target.style.borderColor = '#e0dbd0'}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded cursor-pointer"
                style={{ accentColor: '#e6c84a' }}
              />
              <label htmlFor="remember" className="text-xs cursor-pointer" style={{ color: '#8a8578' }}>
                {t('login.remember')}
              </label>
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all"
              style={{ background: '#e6c84a', color: '#1a1700' }}
              onMouseOver={e => e.currentTarget.style.background = '#d4b53a'}
              onMouseOut={e => e.currentTarget.style.background = '#e6c84a'}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loading size={5} />
                  <span>{t('login.connecting')}</span>
                </div>
              ) : (
                <span>{t('login.signin')}</span>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: '#e0dbd0' }} />
            <span className="text-xs" style={{ color: '#b0a98a' }}>{t('login.or')}</span>
            <div className="flex-1 h-px" style={{ background: '#e0dbd0' }} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs transition-all border"
              style={{ background: '#fff', border: '1.5px solid #e0dbd0', color: '#4a4740' }}
              onMouseOver={e => e.currentTarget.style.background = '#f5f3ee'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Google
            </button>
            <button
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs transition-all border"
              style={{ background: '#fff', border: '1.5px solid #e0dbd0', color: '#4a4740' }}
              onMouseOver={e => e.currentTarget.style.background = '#f5f3ee'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 3.99zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Apple
            </button>
          </div>

          <p className="text-center text-xs mt-6" style={{ color: '#b0a98a' }}>
            {t('login.no.account')}{' '}
            <a href="#" className="font-semibold" style={{ color: '#c4a020' }}>{t('login.signup')}</a>
          </p>
        </div>

        <div
          className="relative hidden md:flex flex-col justify-end p-8 overflow-hidden bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${loginBg})`,
          }}
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full" style={{ background: '#e6c84a', opacity: 0.3 }} />
          <div className="absolute top-10 right-6 w-24 h-24 rounded-full" style={{ background: '#e6c84a', opacity: 0.2 }} />

          <div
            className="absolute top-6 left-6 right-6 rounded-xl p-3.5"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#e6c84a' }}>
                <svg className="w-4 h-4" style={{ color: '#1a1700' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: '#fff' }}>Tableau de bord</p>
                <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.74)' }}>Mise à jour en temps réel</p>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: '#2dce89', color: '#fff' }}>
                ● Live
              </span>
            </div>
          </div>

          <div className="absolute grid grid-cols-3 gap-2" style={{ top: '108px', left: '24px', right: '24px' }}>
            {[
              { value: '94%', label: 'Efficacité', highlight: true },
              { value: '2.4k', label: 'Projets', highlight: false },
              { value: '12', label: 'Équipes', highlight: false },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl p-3 text-center"
                style={{
                  background: stat.highlight ? 'rgba(230,200,74,0.14)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${stat.highlight ? 'rgba(230,200,74,0.28)' : 'rgba(255,255,255,0.09)'}`,
                }}
              >
                <p className="text-lg font-bold leading-none mb-1" style={{ color: stat.highlight ? '#e6c84a' : '#fff' }}>
                  {stat.value}
                </p>
                <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-24">
            <h2 className="text-xl font-bold leading-snug mb-2" style={{ color: '#fff' }}>
              Gérez vos projets{' '}
              <span style={{ color: '#e6c84a' }}>en équipe</span>
            </h2>
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.76)' }}>
              Collaboration en temps réel, suivi de performance, et bien plus encore.
            </p>

            <div className="flex items-end gap-1 mt-5" style={{ height: '40px' }}>
              {[55, 80, 100, 65, 45, 90, 70].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t-sm"
                  style={{
                    height: `${h}%`,
                    background: i === 2 ? '#e6c84a' : 'rgba(255, 255, 255, 0.57)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Toaster richColors />
    </div>
  );
}
